import React, { useState, useEffect } from 'react';
import { useRef } from 'react';
import { useFormik } from 'formik';
import { useParams, useNavigate } from 'react-router-dom';
import { FilePicker } from './FilePicker';
import { DropdownField } from '../../../components/dropdown/DropdownField';
import ApiService from '../../../services/ApiService';
import { ShowErrorToast, ShowSuccessToast } from '../../../components/toaster/toast';
import { DotLoaderComp } from '../../../components/DotLoader/index';

export const UpdatePlanModule = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [premiumFiles, setPremiumFiles] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  // Store existing benefits (from API "benefits") and premiums (from API "premiums")
  const [existingBenefits, setExistingBenefits] = useState(null);
  const [existingPremiums, setExistingPremiums] = useState({});
  // Control modal visibility for benefits and for a selected premium group
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [selectedPremium, setSelectedPremium] = useState(null);

  // Mapping from file names to region_id (as in your API)
  const regionMapping = {
    'Europe': 1,
    'Others WW': 2,
    'Singapore & Hongkong': 3,
    'North America': 4,
    'TAL': 5,
    'All First Care': 7
  };

  const areaOptions = [
    { value: 3, label: 'Worldwide' },
    { value: 4, label: 'ASEAN Excluding Singapore & Hong Kong' },
    { value: 5, label: 'All First Care' },
  ];

  const planTypeOptions = [
    { value: 'inpatient', label: 'Inpatient' },
    { value: 'outpatient', label: 'Outpatient' },
  ];
  
  const coverOptions = [
    { value: 'life_care', label: 'Life Care' },
    { value: 'first_care', label: 'First Care' },
  ];

  const shortBenefitOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: "Optional", label: "Optional" },
  ];

  // Fetch the plan details from the API
  const fetchPlanDetails = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getModulesById(id);
      console.log(response);
      // Set existing benefits from response.data.benefits and premiums from response.data.premiums
      setExistingBenefits(response.data.benefits || null);
      setExistingPremiums(response.data.premiums || {});
      // Set initial values for form (file inputs cannot be pre-populated)
      setInitialValues({
        name: response.data.name || '',
        annual_limit: response.data.annual_limit || '',
        type: response.data.type || 'inpatient',
        cover: response.data.cover || 'life_care',
        area_id: response.data.area_id || 3,
        shortBenefits: {
          evacuation_and_repatriation: response.data.evacuation_and_repatriation || 'yes',
          inpatient_benefits: response.data.inpatient_benefits || 'yes',
          maternity: response.data.maternity || 'yes',
          free_newborn_cover: response.data.free_newborn_cover || 'yes',
          outpatient_benefits: response.data.outpatient_benefits || 'yes',
          vision_benefit: response.data.vision_benefit || 'yes',
          dental_benefit: response.data.dental_benefit || 'yes',
          travel_benefit: response.data.travel_benefit || 'yes',
          medical_checkup_and_vaccinations: response.data.medical_checkup_and_vaccinations || 'yes',
        },
        detailed_benefits: null,
        premiums: [] // User re-uploads premiums if needed
      });
    } catch (error) {
      ShowErrorToast('Failed to fetch plan details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanDetails();
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues || {
      name: '',
      annual_limit: '',
      type: 'inpatient',
      cover: 'Life Care',
      area_id: 3,
      shortBenefits: {
        evacuation_and_repatriation: 'yes',
        inpatient_benefits: 'yes',
        maternity: 'yes',
        free_newborn_cover: 'yes',
        outpatient_benefits: 'yes',
        vision_benefit: 'yes',
        dental_benefit: 'yes',
        travel_benefit: 'yes',
        medical_checkup_and_vaccinations: 'yes',
      },
      detailed_benefits: null,
      premiums: []
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // Convert detailed benefits file to Base64 if provided
        const detailedBenefitsBase64 = values.detailed_benefits
          ? await convertToBase64(values.detailed_benefits)
          : null;
        // Process premium files (convert each to Base64)
        const premiumsProcessed = values.premiums && values.premiums.length > 0
          ? await Promise.all(
              values.premiums.map(async (premium) => ({
                fileName: premium.fileName,
                content: await convertToBase64(premium.file),
                region_id: premium.region_id,
                // area_id: values.area_id,
              }))
            )
          : [];
  
        const payload = {
          ...values,
          detailed_benefits: detailedBenefitsBase64 ? { content: detailedBenefitsBase64 } : null,
          premiums: premiumsProcessed,
        };
  
        await ApiService.updateModule(id, payload);
        ShowSuccessToast('Plan updated successfully');
        // Optionally navigate to another page:
        navigate('/plans');
      } catch (error) {
        ShowErrorToast('Failed to update plan');
      } finally {
        setLoading(false);
      }
    },
  });

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  // Update premium file list based on area_id and clear previously selected premiums


  // In your component:
  const initialAreaRef = useRef(formik.values.area_id);
  
  useEffect(() => {
    const updatePremiumFiles = () => {
      let files = [];
      if (formik.values.area_id === 3) {
        files = ['Europe', 'Others WW', 'Singapore & Hongkong', 'North America'];
      } else if (formik.values.area_id === 4) {
        files = ['TAL'];
      } else if (formik.values.area_id === 5) {
        files = ['All First Care'];
      }
      setPremiumFiles(files);
      
      // Only clear premiums if the area_id has actually changed after initial load.
      if (formik.values.area_id !== initialAreaRef.current) {
        formik.setFieldValue('premiums', []);
      }
    };
    updatePremiumFiles();
  }, [formik.values.area_id]);

  const filteredAreaOptions =
  formik.values.cover === "Life Care"
    ? areaOptions.filter((option) => option.value === 3 || option.value === 4)
    : // Assuming "life_fire_care" (first care) should only allow option 5
      areaOptions.filter((option) => option.value === 5);

      useEffect(() => {
        if (!filteredAreaOptions.some((option) => option.value === formik.values.area_id)) {
          formik.setFieldValue("area_id", filteredAreaOptions[0].value);
        }
      }, [formik.values.cover, filteredAreaOptions]);   
  

  if (loading && !initialValues) {
    return <DotLoaderComp />;
  }

  return (
    <div className='bg-[#F9F8F5] px-5 py-7'>
      <h1 className='text-[#012949] font-semibold text-[24px] mb-4'>
        Update Plan with Premiums and Benefits
      </h1>
      <div className='bg-white px-5 py-7 rounded-lg min-h-[50vh]'>
        <form onSubmit={formik.handleSubmit}>
          <div className='grid grid-cols-2 gap-6 mb-4'>
            <div>
              <label htmlFor="name" className="block mb-0 text-[14px] font-semibold">
                Plan Name
              </label>
              <input 
                type='text'
                id="name"
                name='name'
                placeholder='Plan Name'
                value={formik.values.name}
                onChange={formik.handleChange}
                className='border p-2 rounded w-full'
              />
            </div>
            <div>
              <label htmlFor="annual_limit" className="block mb-0 text-[14px] font-semibold">
                Annual Limit
              </label>
              <input 
                type='number'
                id="annual_limit"
                name='annual_limit'
                placeholder='Annual Limit'
                value={formik.values.annual_limit}
                onChange={formik.handleChange}
                className='border p-2 rounded w-full'
              />
            </div>
            <DropdownField
              label='Plan Type'
              options={planTypeOptions}
              value={formik.values.type}
              onChange={(e) => formik.setFieldValue('type', e.target.value)}
            />
            <DropdownField
              label='Cover'
              options={coverOptions}
              value={formik.values.cover}
              onChange={(e) => formik.setFieldValue('cover', e.target.value)}
            />
            <DropdownField
              label='Area of Cover'
              options={areaOptions}
              value={formik.values.area_id}
              onChange={(e) => formik.setFieldValue('area_id', Number(e.target.value))}
            />
          </div>
          
          <h2 className='text-lg font-medium mb-4'>Short Benefits</h2>
          <div className='grid grid-cols-3 gap-4'>
            {Object.keys(formik.values.shortBenefits).map((key) => (
              <DropdownField
                key={key}
                label={key.replace('_', ' ')}
                options={shortBenefitOptions}
                value={formik.values.shortBenefits[key]}
                onChange={(e) => formik.setFieldValue(`shortBenefits.${key}`, e.target.value)}
              />
            ))}
          </div>
          
          <h2 className='text-lg font-medium mt-6 mb-4'>Detailed Benefits</h2>
          <FilePicker
            title='Upload Detailed Benefits CSV'
            fileType="benefits"
            onChange={(e) => formik.setFieldValue('detailed_benefits', e.target.files[0])}
          />
           <button
              type="button"
              className="mt-2 flex items-end justify-end  text-black rounded"
              onClick={() => setShowBenefitsModal(true)}
            >
              View Benefits
            </button>

   <h2 className='text-lg font-medium mt-6 mb-4'>Premiums</h2>
   <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    {premiumFiles.map((fileName) => {
    const regionId = regionMapping[fileName];
    return (
      <div key={fileName} className="">
        <FilePicker
          title={`Upload ${fileName} CSV`}
          fileType="premiums"
          onChange={(e) => {
            formik.setFieldValue('premiums', [
              ...formik.values.premiums,
              {
                fileName,
                file: e.target.files[0],
                region_id: regionId
              }
            ]);
          }}
        />
        {existingPremiums[regionId] && existingPremiums[regionId].length > 0 && (
          <button
            type="button"
            className="mt-2 flex items-end justify-end text-black rounded"
            onClick={() => setSelectedPremium({ regionId, fileName })}
          >
            view rates
          </button>
        )}
      </div>
    );
  })}
</div>

          {/* Buttons to view existing benefits and each premium rate (if available) */}
          {/* <div className="mt-4 flex flex-wrap gap-4">
            <button
              type="button"
              className="px-4 py-2 bg-green-500 text-white rounded"
              onClick={() => setShowBenefitsModal(true)}
            >
              View Benefits
            </button>
            {premiumFiles.map((fileName) => {
              const regionId = regionMapping[fileName];
              const premiumsForRegion = existingPremiums[regionId];
              return (
                premiumsForRegion && premiumsForRegion.length > 0 && (
                  <button
                    key={fileName}
                    type="button"
                    className="px-4 py-2 bg-green-500 text-white rounded"
                    onClick={() => setSelectedPremium({ regionId, fileName })}
                  >
                    View {fileName} Rate
                  </button>
                )
              );
            })}
          </div> */}

          <div className='pt-6'>
            <button
              type="submit"
              className='w-[160px] h-[46px] bg-orangeColor rounded-[10px] font-inter text-base font-bold text-white'
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Module'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal for Existing Benefits */}
      {showBenefitsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-md w-3/4 md:w-1/2">
            <h2 className="text-lg font-semibold mb-4">Existing Benefits</h2>
            <div className="max-h-96 overflow-y-auto bg-gray-100 p-4 rounded">
              <pre className="text-sm">
                {existingBenefits
                  ? JSON.stringify(existingBenefits, null, 2)
                  : 'No benefits available.'}
              </pre>
            </div>
            <button
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
              onClick={() => setShowBenefitsModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Modal for a Selected Premium Group */}
      {selectedPremium && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-md w-3/4 md:w-1/2">
            <h2 className="text-lg font-semibold mb-4">
              Existing {selectedPremium.fileName} Premium Rates
            </h2>
            <div className="max-h-96 overflow-y-auto bg-gray-100 p-4 rounded">
              <pre className="text-sm">
                {existingPremiums[selectedPremium.regionId]
                  ? JSON.stringify(existingPremiums[selectedPremium.regionId], null, 2)
                  : 'No premium rates available.'}
              </pre>
            </div>
            <button
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
              onClick={() => setSelectedPremium(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
